import { useState, useEffect } from 'react';
import AuthShell from './ui/AuthShell';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useToast } from '../contexts/ToastContext';
import {
  clearPasswordRecoveryActive,
  formatRecoveryErrorMessage,
  getRecoveryUrlError,
  hasRecoveryUrlParams,
  markPasswordRecoveryActive,
} from '../utils/authRecovery';
import Button from './ui/Button';
import PasswordInput from './ui/PasswordInput';
import PasswordRules from './ui/PasswordRules';
import { friendlyAuthError, isPasswordAcceptable } from '../lib/authValidation';

const RECOVERY_VERIFY_TIMEOUT_MS = 5000;
const RECOVERY_VERIFY_TIMEOUT_NO_PARAMS_MS = 2500;

export default function ResetPassword() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyState, setVerifyState] = useState('verifying');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const urlError = getRecoveryUrlError();
    if (urlError) {
      if (import.meta.env.DEV) {
        console.warn('[auth] recovery link error:', urlError.errorCode, urlError);
      }
      setVerifyState('invalid');
      setError(formatRecoveryErrorMessage(urlError));
      return undefined;
    }

    let cancelled = false;
    let resolved = false;
    const hasRecoveryParams = hasRecoveryUrlParams();

    const resolveReady = () => {
      if (cancelled || resolved) return;
      resolved = true;
      markPasswordRecoveryActive();
      setVerifyState('ready');
      setError('');
    };

    const resolveInvalid = (message) => {
      if (cancelled || resolved) return;
      resolved = true;
      setVerifyState('invalid');
      setError(message || 'This reset link is invalid or has expired.');
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled || !session) return;
      if (event === 'PASSWORD_RECOVERY') {
        resolveReady();
      }
    });

    const timeoutMs = hasRecoveryParams
      ? RECOVERY_VERIFY_TIMEOUT_MS
      : RECOVERY_VERIFY_TIMEOUT_NO_PARAMS_MS;

    const timeout = setTimeout(async () => {
      if (cancelled || resolved) return;

      if (hasRecoveryParams) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled || resolved) return;
        if (session) {
          resolveReady();
        } else {
          resolveInvalid();
        }
        return;
      }

      resolveInvalid();
    }, timeoutMs);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordAcceptable(password)) {
      setError('Your new password needs everything on the list.');
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      // The link proved it is you; stay signed in. The app ignores recovery sessions until this flag clears.
      clearPasswordRecoveryActive();
      showToast("Password updated. You're signed in.");
      navigate('/', { replace: true });
    } catch (err) {
      setError(friendlyAuthError(err, 'Could not update your password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="text-xl font-semibold text-gray-900 dark:text-dark-text text-center mb-6">
        Reset password
      </h1>

      {verifyState === 'ready' ? (
        <>
          <p className="mb-4 text-sm text-center text-gray-500 dark:text-dark-muted">
            Choose a new password for your account.
          </p>
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            <PasswordInput
              name="password-reset"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              visible={showPassword}
              onToggleVisible={() => setShowPassword((v) => !v)}
              aria-describedby="link-reset-password-rules"
              disabled={loading}
            />
            <PasswordRules id="link-reset-password-rules" password={password} />
            <PasswordInput
              name="password-reset-confirm"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              visible={showPassword}
              showToggle={false}
              invalid={confirmPassword.length > 0 && !password.startsWith(confirmPassword)}
              disabled={loading}
            />
            {confirmPassword.length > 0 && !password.startsWith(confirmPassword) && (
              <p className="auth-field-hint auth-field-hint--error">Passwords don&apos;t match.</p>
            )}
            <Button type="submit" fullWidth loading={loading} size="lg">
              Update password
            </Button>
          </form>
          {error && <p className="mt-4 text-sm text-center text-danger">{error}</p>}
          <p className="mt-4 text-center">
            <Link
              to="/login"
              className="text-sm text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text"
            >
              ← Back to log in
            </Link>
          </p>
        </>
      ) : (
        <div className="text-center">
          <p
            className={`text-sm ${
              verifyState === 'invalid' ? 'text-danger ' : 'text-gray-500 dark:text-dark-muted'
            }`}
          >
            {error || 'Verifying your reset link…'}
          </p>
          {verifyState === 'invalid' && (
            <div className="mt-4 flex flex-col items-center gap-3">
              <Button type="button" variant="primary" fullWidth onClick={() => navigate('/login')}>
                Back to log in
              </Button>
              <Link
                to="/login"
                className="text-sm text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text"
              >
                Request a new reset link
              </Link>
            </div>
          )}
        </div>
      )}
    </AuthShell>
  );
}
